import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { List, Map } from 'immutable';
import { extend, isUndefined } from 'lodash';
import { Subject } from 'rxjs';
import { DialogService } from '../../../dialog/dialog.service';
import {
  SortByEnum,
  SortOrderEnum,
} from '../../../entity-data/entity-data.data';
import { BannerCanvasTypesEnum } from '../../../features-data/features-data.data';
import { FeaturesDataStore } from '../../../features-data/features-data.store';
import { INoItemsOptions } from '../../../feed/feed.interface';
import { ISelectOption } from '../../../form-master/components/select-field/select-field.interface';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../../../shared/components/icon/icons-sizes.enum';
import {
  I2gNgTrackingActionBuilder,
  ITrackingProperties,
} from 'i2g-ng-tracking';
import { TrackingAdditionalPropertiesBuilder } from '../../../tracking/tracking-additional-properties.builder';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { IMasterListHeader } from './master-list.interface';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-master-list',
  templateUrl: 'master-list.component.html',
  styleUrls: ['master-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MasterListComponent implements OnChanges, OnInit, OnDestroy {
  @Input() items: List<any>;
  @Input() forceDisplayFilters = false;
  @Input() noItemsOptions: INoItemsOptions;
  @Input() groupByFn: Function;
  @Input() sortGroupsByFn: Function;
  @Input() sortGroupsComparator: Function;
  @Input() groupMetadata: Map<string, string>;
  @Input() headers: List<IMasterListHeader>;
  @Input() sortByHeader: IMasterListHeader;
  @Input() showHeader: boolean;
  @Input() hasFilters: boolean;
  @Input() enableBulkActions: boolean;
  @Input() filterOptions: List<ISelectOption>;
  @Input() selectedFilter: ISelectOption;
  @Input() fetchingFilterTotals: boolean;
  @Input() fetchingList: boolean;
  @Input() filterTotals: string;
  @Input() filterTotalsLabel: string;
  @Input() filterBlocker: (
    filterOption: ISelectOption
  ) => Promise<ISelectOption>;
  @Input() filterTrackingIdentifier: string;
  @Input() filterTrackingAdditionalProperties: any;
  @Input() trackingObject: ITrackingObject;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() lastPageFetched: boolean;
  @Input() filterCtaLabel: string;
  @Input() filterCtaTrackingAction: ITrackingAction;
  @Input() enablePagination: boolean;
  @Input() displayEmptyGroups: boolean;
  @Input() trackByFn: Function = this.trackByLocalId;
  @Input() featureName: string;
  @Input() disableFilterCta: boolean;
  @Input() trackingAdditionalProperties: ITrackingProperties;

  @Output() onFilterCta: EventEmitter<void> = new EventEmitter<void>();
  @Output() onInfinitePagination: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  onToggleSorting: EventEmitter<IMasterListHeader> = new EventEmitter<IMasterListHeader>();
  @Output() onFilterList: EventEmitter<any> = new EventEmitter<any>();
  @Output()
  onNoItemsPrimaryActionClick: EventEmitter<void> = new EventEmitter<void>();

  @ContentChild(TemplateRef, { static: false }) template: TemplateRef<any>;

  private _destroyed: Subject<boolean> = new Subject<boolean>();

  chevronDownIcon: IIcon;
  SortOrderEnum: typeof SortOrderEnum;
  SortByEnum: typeof SortByEnum;
  loading = true;
  filterControl: FormControl;
  filterButtonTapped: ITrackingAction;
  filterDropdownSelected: ITrackingAction;
  loadingSpinnerIcon: IIcon;
  showPremiumBanner: boolean;

  constructor(
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _featuresDataStore: FeaturesDataStore,
    private _dialogService: DialogService,
    private _trackingAdditionalPropertiesBuilder: TrackingAdditionalPropertiesBuilder
  ) {}

  ngOnChanges(changes) {
    const selectedFilterChanges = changes.selectedFilter;

    this.loading = !this.items;

    if (selectedFilterChanges && this.selectedFilter) {
      if (!this.filterControl) {
        this.filterControl = new FormControl(this.selectedFilter.value);

        this.filterControl.valueChanges
          .pipe(
            filter((x) => !!x),
            takeUntil(this._destroyed)
          )
          .subscribe((value: any) => this.onFilterList.emit(value));
      } else {
        const previousFilter: ISelectOption =
          selectedFilterChanges.previousValue;

        if (this.selectedFilter.key !== previousFilter.key) {
          // setting a timeout here so filterControl.writeValue() happens after selectField.ngOnChanges()
          setTimeout(() =>
            this.filterControl.setValue(this.selectedFilter.value, {
              emitEvent: false,
            })
          );
        }
      }
    }
  }

  ngOnInit() {
    this.SortOrderEnum = SortOrderEnum;
    this.SortByEnum = SortByEnum;
    this.chevronDownIcon = {
      name: 'mini-chevron-down',
      size: IconSizesEnum.custom,
    };
    this.loadingSpinnerIcon = {
      name: 'loading-spinner',
      size: IconSizesEnum.medium,
    };
    this._featuresDataStore
      .hasFeatureHighlightCanvas(this.featureName)
      .pipe(takeUntil(this._destroyed))
      .subscribe((hasFeatureHighlight: boolean) => {
        this.showPremiumBanner = hasFeatureHighlight;
      });

    if (!!this.filterTrackingIdentifier && !!this.trackingScreenName) {
      this.filterButtonTapped = this._trackingActionBuilder.buildButtonTapped(
        this.filterTrackingIdentifier,
        this.trackingScreenName
      );
      this.filterDropdownSelected = this._trackingActionBuilder.buildDropdownSelected(
        this.filterTrackingIdentifier,
        this.trackingScreenName
      );
    }

    if (this.headers) {
      this.headers
        .filter(
          (header) => !isUndefined(header.sortBy) && !header.disableSorting
        )
        .forEach((header) => {
          header.trackingAdditionalProperties = this._trackingAdditionalPropertiesBuilder.buildSortOptions(
            header.sortBy,
            header.sortOrder
          );
        });
    }
  }

  trackByLocalId(index: number, feedItem: any): string {
    const localId = feedItem.localId;

    return localId ? localId : feedItem.id;
  }

  toggleSorting(header: IMasterListHeader) {
    if (!isUndefined(header.sortBy) && !header.disableSorting) {
      const sortBy: SortByEnum = header.sortBy;
      let newSortOrder: SortOrderEnum;

      if (this.sortByHeader && sortBy === this.sortByHeader.sortBy) {
        newSortOrder =
          this.sortByHeader.sortOrder === SortOrderEnum.desc
            ? SortOrderEnum.asc
            : SortOrderEnum.desc;
      } else {
        newSortOrder = header.sortOrder;
      }

      this.onToggleSorting.emit(
        extend({}, header, { sortOrder: newSortOrder })
      );

      // Click listener for trackable directive is triggered before this method (also a click handler) is called.
      // Setting additional tracking properties now for future tracking record of next click event.
      const nextSortOrder =
        newSortOrder === SortOrderEnum.desc
          ? SortOrderEnum.asc
          : SortOrderEnum.desc;

      header.sortOrder = newSortOrder;
      header.trackingAdditionalProperties = this._trackingAdditionalPropertiesBuilder.buildSortOptions(
        header.sortBy,
        nextSortOrder
      );
    }
  }

  showFeatureHighlightDialog() {
    this._dialogService.openGrowthDialog(
      BannerCanvasTypesEnum.feature_highlight,
      this.featureName
    );
  }

  ngOnDestroy() {
    this._dialogService.closeGrowthDialog(
      BannerCanvasTypesEnum.feature_highlight,
      this.featureName
    );

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
